/*
 * Речник на покривните термини.
 *
 * Един източник за всичко: хъбът /rechnik, отделните страници, JSON-LD и
 * файлът rechnik-verify.md се правят от този файл. Затова описанието в схемата
 * съвпада дума по дума с видимото определение.
 *
 * Правилото на речника: всеки термин има един собственик. Ако терминът е на
 * услуга или статия, речникът само води към нея и пише от друг ъгъл — какво
 * значи редът в офертата и какво да се уточни с изпълнителя.
 *
 * `verify: true` значи "VERIFY": значението е регионално или несигурно и трябва
 * да го потвърди изпълнителят, преди фазата да се публикува.
 */

export type ClusterKey =
  | 'konstrukciya'
  | 'vidove'
  | 'pokritiya'
  | 'izolaciya'
  | 'lamarina'
  | 'uluci'
  | 'aksesoari'
  | 'fizika';

export type Owner =
  /** Терминът има собствена страница в речника (★). */
  | { kind: 'page' }
  /** Страница на услуга. `href: null` = такава услуга още няма (TODO). */
  | { kind: 'service'; name: string; href: string | null }
  /** Статия в блога. `href: null` = още не е публикувана (TODO). */
  | { kind: 'blog'; title: string; href: string | null }
  /** Без собственик: терминът живее само в хъба. */
  | { kind: 'hub' };

export interface GlossaryTerm {
  slug: string;
  term: string;
  cluster: ClusterKey;
  synonyms?: string[];
  definition: string;
  owner: Owner;
  verify?: boolean;
  /** Бележка за изпълнителя, излиза само в rechnik-verify.md. */
  verifyNote?: string;
}

export interface GlossaryPage {
  slug: string;
  priority: 'P1' | 'P2' | 'P3';
  metaDescription: string;
  /** Как терминът се среща в текста на услугите, за връзката при първото споменаване. */
  mention: RegExp;
  sections: { heading: string; paragraphs: string[] }[];
  related: string[];
  /** Връзки към услуги, които не са термини в речника. */
  extraLinks?: { label: string; href: string }[];
  service: { name: string; href: string | null };
  verify?: boolean;
}

/** Датата на последната редакция, за sitemap. */
export const GLOSSARY_UPDATED = '2026-10-02';

export const GLOSSARY_TITLE = 'Речник на покривните термини';

/*
 * Спецификацията иска „… | [бранд]“, но с марката заглавието минава 80 знака,
 * а сайтът реже заглавията на 65. Марката отпада, ключовата част остава цяла.
 */
export const GLOSSARY_META_TITLE = 'Речник на покривните термини – ремонт на покриви и улуци';

export const GLOSSARY_META_DESCRIPTION =
  'Какво значат думите в офертата за ремонт на покрив и улуци: мертек, атика, надулучна пола, холкер и още. Кратки обяснения, подредени по теми.';

/** Уводът на хъба. Влиза и като описание на DefinedTermSet. */
export const GLOSSARY_INTRO =
  'Офертата за покрив е пълна с думи, които майсторите ползват всеки ден, а собственикът на къщата чува за първи път. Тук са обяснени най-честите, подредени по теми, за да знаете какво подписвате.';

export const CLUSTERS: { key: ClusterKey; title: string; intro: string }[] = [
  { key: 'konstrukciya', title: 'Покривна конструкция', intro: 'Дървото и бетонът, които носят покрива: греди, подпори и скарата, на която лягат керемидите.' },
  { key: 'vidove', title: 'Видове покриви', intro: 'Как се наричат покривите според формата и броя на скатовете.' },
  { key: 'pokritiya', title: 'Покривни покрития', intro: 'Външният пласт, който се вижда отвън, и елементите по билото.' },
  { key: 'izolaciya', title: 'Изолационни слоеве и хидроизолация', intro: 'Пластовете под покритието, които спират водата, парата и студа.' },
  { key: 'lamarina', title: 'Ламаринени елементи и обшивки', intro: 'Металните детайли по ръбовете и преходите, откъдето покривът тече най-често.' },
  { key: 'uluci', title: 'Улуци и отводняване', intro: 'Всичко, което събира водата от покрива и я отвежда далеч от къщата.' },
  { key: 'aksesoari', title: 'Комин, отвори и аксесоари', intro: 'Елементите, които пробиват покрива или стоят върху него.' },
  { key: 'fizika', title: 'Помещения и физика на покрива', intro: 'Пространството под покрива и какво става в него с топлината и влагата.' },
];

/* ---------- Собственици ---------- */

const own: Owner = { kind: 'page' };
const hub: Owner = { kind: 'hub' };
const svc = (name: string, href: string | null): Owner => ({ kind: 'service', name, href });
const blog = (title: string, href: string | null = null): Owner => ({ kind: 'blog', title, href });

const S = {
  konstrukciya: svc('Покривна конструкция', '/izgrazhdane-na-pokrivi/pokrivna-konstrukciya'),
  betonenPoyas: svc('Бетонен пояс на покрив', '/izgrazhdane-na-pokrivi/betonen-poyas'),
  skatni: svc('Скатни покриви', '/izgrazhdane-na-pokrivi/skatni-pokrivi'),
  lamarina: svc('Ламарина', '/pokrivni-pokritiya/lamarina'),
  keremidi: svc('Керемиди', '/pokrivni-pokritiya/keremidi'),
  kapaci: svc('Подмазване на капаци', '/remont-na-pokrivi/podmazvane-na-kapaci'),
  metalniKeremidi: svc('Метални керемиди', '/pokrivni-pokritiya/metalni-keremidi'),
  bitumniKeremidi: svc('Битумни керемиди', '/pokrivni-pokritiya/bitumni-keremidi'),
  termopaneli: svc('Термопанели', '/pokrivni-pokritiya/termopaneli'),
  toploizolaciya: svc('Топлоизолация на покрив', '/toploizolaciya-na-pokriv'),
  hidroNaPokriv: svc('Хидроизолация на покрив', '/hidroizolaciya/na-pokriv'),
  bitumna: svc('Битумна хидроизолация', '/hidroizolaciya/bitumna'),
  technaGuma: svc('Течна гума', '/hidroizolaciya/techna-guma'),
  obshivkiStrehi: svc('Обшивки на стрехи и бордове', '/tenekedzhiyski-uslugi/obshivki-strehi-bordove'),
  obshivkaKomin: svc('Обшивка на комин', '/tenekedzhiyski-uslugi/obshivka-na-komin'),
  tenekedzhiyski: svc('Тенекеджийски услуги', '/tenekedzhiyski-uslugi'),
  uluci: svc('Улуци и водостоци', '/uluci'),
  bezshevni: svc('Безшевни улуци', '/uluci/bezshevni'),
  pvc: svc('PVC улуци', '/uluci/pvc'),
  pocinkovani: svc('Поцинковани улуци', '/uluci/pocinkovani'),
  vodostochni: svc('Водосточни тръби', '/uluci/vodostochni-trabi'),
  kapanduri: svc('Капандури', '/tenekedzhiyski-uslugi/kapanduri'),
  snegozadarzhateli: svc('Снегозадържатели', '/tenekedzhiyski-uslugi/snegozadarzhateli'),
  navesi: svc('Навеси', '/navesi'),
  demontazh: svc('Демонтаж на покрив и извозване', '/izgrazhdane-na-pokrivi/demontazh-na-pokriv'),
};

/*
 * Статиите. Непубликуваните са TODO: терминът стои в хъба без връзка, докато
 * статията не излезе. Когато излезе, тук се попълва адресът.
 */
const B = {
  dvuskaten: blog('Двускатен покрив', '/blog/dvuskaten-pokriv'),
  letvi: blog('Летви и скара под керемидите'), // TODO: непубликувана
  // Спецификацията я води като „Нов покрив: всички етапи от конструкцията до керемидите“.
  novPokriv: blog('Как се прави нов покрив', '/blog/kak-se-pravi-nov-pokriv'),
  vidove: blog('Видове покриви на къщи', '/blog/vidove-pokrivi'),
  kondenz: blog('Конденз и мухъл под покрива'), // TODO: непубликувана
  membrana: blog('Подпокривна мембрана: дишаща или битумна'), // TODO: непубликувана
  izolaciya: blog('Изолация на подпокривното пространство'), // TODO: непубликувана
  ulama: blog('Улама: какво е и защо тече'), // TODO: непубликувана
  voronki: blog('Воронки и отводняване на плосък покрив'), // TODO: непубликувана
  ulucite: blog('Улуци: почистване, течове и кога се сменят'), // TODO: непубликувана
  shapka: blog('Шапка за комин: видове и защо е нужна'), // TODO: непубликувана
  snyag: blog('Сняг и лед по покрива'), // TODO: непубликувана
  tech: blog('Откъде тече покривът', '/blog/otkade-teche-pokrivat'),
};

/* ---------- Термините ---------- */

export const TERMS: GlossaryTerm[] = [
  /* 4.1 Покривна конструкция */
  {
    slug: 'gredored', term: 'Гредоред', cluster: 'konstrukciya', owner: own, verify: true, // VERIFY
    definition: 'Гредоредът е редът от успоредни дървени греди, които носят пода на тавана или междуетажната конструкция. Върху него лежи подът на мансардата, а отдолу се закрепва таванът на последния етаж.',
  },
  {
    slug: 'rigel', term: 'Ригел', cluster: 'konstrukciya', owner: own, verify: true, // VERIFY
    definition: 'Ригелът е хоризонтална греда, която свързва вертикалните или наклонените елементи на покривната конструкция и не им дава да се разместят. В покрива обикновено лежи върху столиците или свързва двойка мертеци.',
  },
  {
    slug: 'mertek', term: 'Мертек', cluster: 'konstrukciya', owner: own, verify: true, // VERIFY
    definition: 'Мертекът е наклонената греда, която върви от билото до стрехата и носи покритието. Мертеците стоят успоредно на равни разстояния, а върху тях се заковават контралетвите и летвите.',
  },
  {
    slug: 'pokrivna-konstruktsiya', term: 'Покривна конструкция', cluster: 'konstrukciya', synonyms: ['покривна ферма'], owner: S.konstrukciya,
    definition: 'Покривната конструкция е дървеният скелет, който носи целия покрив. В офертата проверете дали пише само „преглед“, или изрично смяна на елементи, и как ще се смята дървото, ако при свалянето излезе гнило.',
  },
  {
    slug: 'mauerlat', term: 'Мауерлат', cluster: 'konstrukciya', owner: S.konstrukciya,
    definition: 'Мауерлатът е гредата, легнала върху стената или бетонния пояс, на която стъпват долните краища на мертеците. Ако офертата предвижда смяната му, питайте как ще се закрепи към стената.',
  },
  {
    slug: 'stolitsa', term: 'Столица', cluster: 'konstrukciya', synonyms: ['подпора'], owner: S.konstrukciya, verify: true, // VERIFY
    definition: 'Столицата е вертикалната подпора, която държи билната греда или ригела. При цялостен ремонт питайте дали столиците се проверяват и какво става, ако някоя е изгнила в основата.',
  },
  {
    slug: 'kleshti', term: 'Клещи', cluster: 'konstrukciya', owner: S.konstrukciya, verify: true, // VERIFY
    definition: 'Клещите са две дъски, захванати от двете страни на мертеците, които ги стягат хоризонтално и не дават на покрива да се разтвори. Срещат се в оферти за укрепване на стара конструкция.',
  },
  {
    slug: 'rebro', term: 'Ребро', cluster: 'konstrukciya', synonyms: ['ребра на покрива'], owner: B.dvuskaten,
    definition: 'Реброто е наклоненият външен ръб, където се срещат два ската на четирискатен покрив. По него се редят капаци, както по билото.',
  },
  {
    slug: 'letva', term: 'Летва', cluster: 'konstrukciya', owner: B.letvi,
    definition: 'Летвата е тесен дървен прът, закован напречно на мертеците, на който се закачат керемидите. Разстоянието между летвите зависи от вида керемида, затова в офертата трябва да е ясно дали се сменят.',
  },
  {
    slug: 'kontraletva', term: 'Контралетва', cluster: 'konstrukciya', owner: B.letvi,
    definition: 'Контралетвите се заковават по дължината на мертеците, върху мембраната, и вдигат летвите над нея. Така под покритието остава въздушен канал. Ако ги няма в офертата за нов покрив, питайте защо.',
  },
  {
    slug: 'skara', term: 'Скара', cluster: 'konstrukciya', synonyms: ['двойна скара', 'покривна скара'], owner: B.letvi,
    definition: 'Скарата е общото име на летвите и контралетвите заедно, тоест решетката, на която лежи покритието. „Двойна скара“ в офертата значи, че има и контралетви, а не само летви.',
  },
  {
    slug: 'dashchena-obshivka', term: 'Дъсчена обшивка', cluster: 'konstrukciya', synonyms: ['дъсчена основа', 'OSB обшивка'], owner: B.novPokriv,
    definition: 'Дъсчената обшивка е плътен под от дъски или OSB плоскости, закован върху мертеците. Нужна е под битумни керемиди и фалцова ламарина, които сами нямат твърдост.',
  },
  {
    slug: 'betonen-poyas', term: 'Бетонен пояс', cluster: 'konstrukciya', owner: S.betonenPoyas,
    definition: 'Бетонният пояс е стоманобетонната греда по горния ръб на стените, на която стъпва покривната конструкция. Той разпределя тежестта на покрива равномерно по зида.',
  },

  /* 4.2 Видове покриви */
  {
    slug: 'skaten-pokriv', term: 'Скатен покрив', cluster: 'vidove', owner: S.skatni,
    definition: 'Скатният покрив има поне един наклонен скат, по който водата се оттича сама. Броят и формата на скатовете казват колко улами, ребра и капаци ще има, а оттам и обемът на работата в офертата.',
  },
  {
    slug: 'dvuskaten-pokriv', term: 'Двускатен покрив', cluster: 'vidove', owner: B.dvuskaten,
    definition: 'Двускатният покрив има два ската, които се срещат в било, и два триъгълни калкана отстрани. Това е най-простата форма за ремонт, защото няма улами и ребра.',
  },
  {
    slug: 'ednoskaten-pokriv', term: 'Едноскатен покрив', cluster: 'vidove', synonyms: ['покрив с един скат'], owner: B.vidove,
    definition: 'Едноскатният покрив има един скат, наклонен в една посока. Среща се при гаражи, навеси и пристройки, а водата се събира в един улук от ниската страна.',
  },
  {
    slug: 'chetiriskaten-pokriv', term: 'Четирискатен покрив', cluster: 'vidove', owner: B.vidove,
    definition: 'Четирискатният покрив има четири ската, които се срещат в било и ребра. Няма калкани, затова стрехата обикаля цялата къща.',
  },
  {
    slug: 'piramidalen-pokriv', term: 'Пирамидален покрив', cluster: 'vidove', owner: B.vidove,
    definition: 'Пирамидалният покрив има четири еднакви триъгълни ската, които се срещат в една точка на върха. Слага се над квадратни сгради и няма било.',
  },
  {
    slug: 'mansarden-pokriv', term: 'Мансарден покрив', cluster: 'vidove', owner: B.vidove,
    definition: 'Мансардният покрив е начупен скатен покрив с по-стръмна долна част, която оставя място за живеене под него. Не го бъркайте с мансардата, която е самото помещение.',
  },
  {
    slug: 'plosak-pokriv', term: 'Плосък покрив', cluster: 'vidove', owner: B.vidove,
    definition: 'Плоският покрив е почти хоризонтален, с наклон от няколко градуса, колкото водата да стигне до воронките. Покрива се с хидроизолация, а не с керемиди.',
  },
  {
    slug: 'naklon-na-pokriva', term: 'Наклон на покрива', cluster: 'vidove', synonyms: ['покривен наклон'], owner: B.dvuskaten,
    definition: 'Наклонът на покрива е ъгълът на ската спрямо хоризонталата, в градуси или в проценти. От него зависи кое покритие е възможно и колко по-голяма е площта на покрива от застроената площ.',
  },
  {
    slug: 'kalkan', term: 'Калкан', cluster: 'vidove', owner: B.dvuskaten, verify: true, // VERIFY
    verifyNote: 'Ползва се и за глуха стена към съседен имот, и за триъгълната стена под двускатен покрив. Кое значение да остане водещо?',
    definition: 'Калканът е глуха стена без прозорци, най-често триъгълната страна под двускатен покрив или стената към съседната сграда. В оферта за ремонт се среща като обшивка или измазване на калкана.',
  },
  {
    slug: 'fronton', term: 'Фронтон', cluster: 'vidove', owner: B.dvuskaten, verify: true, // VERIFY
    verifyNote: 'Разликата с калкан: в разговор майсторите често ги ползват като едно и също.',
    definition: 'Фронтонът е триъгълната горна част на фасадата под двускатен покрив, оформена като украса. В разговор майсторите често го наричат калкан.',
  },
  {
    slug: 'faltsov-pokriv', term: 'Фалцов покрив', cluster: 'vidove', owner: S.lamarina,
    definition: 'Фалцовият покрив е от ламаринени ленти, свързани по дължина с прегънати и притиснати ръбове, без видими винтове. В офертата проверете дебелината на ламарината и дали фалцът се затваря с машина.',
  },
  {
    slug: 'ventiliran-pokriv', term: 'Вентилиран покрив', cluster: 'vidove', owner: B.kondenz,
    definition: 'Вентилираният покрив има въздушен канал между топлоизолацията и покритието, през който влагата излиза навън. Каналът започва при стрехата и свършва при билото.',
  },

  /* 4.3 Покривни покрития */
  {
    slug: 'eternit', term: 'Етернит', cluster: 'pokritiya', synonyms: ['етернитови плоскости'], owner: own, verify: true, // VERIFY
    verifyNote: 'Безопасност: проверете формулировката за свалянето от специализирана фирма.',
    definition: 'Етернитът е вълнообразна или плоска плоскост от азбестоцимент, слагана широко по покриви през миналия век. Съдържа азбест, затова не се реже, не се чупи и не се сваля самостоятелно.',
  },
  {
    slug: 'bilo', term: 'Било', cluster: 'pokritiya', synonyms: ['билна линия'], owner: own,
    definition: 'Билото е най-горният хоризонтален ръб на покрива, където се срещат два ската. По него се редят капаци, а под тях остава процеп, през който излиза въздухът изпод покритието.',
  },
  {
    slug: 'keremida', term: 'Керемида', cluster: 'pokritiya', synonyms: ['цигли'], owner: S.keremidi,
    definition: 'Керемидата е отделната плочка, от която се реди покритието. В офертата търсете производителя, модела и цвета и дали керемидите са нови, или старите се пренареждат.',
  },
  {
    slug: 'keramichna-keremida', term: 'Керамична керемида', cluster: 'pokritiya', owner: S.keremidi,
    definition: 'Керамичната керемида е от печена глина и пази цвета си с десетилетия. Тежи повече от металната, затова конструкцията трябва да е проверена за товара.',
  },
  {
    slug: 'betonna-keremida', term: 'Бетонна керемида', cluster: 'pokritiya', owner: S.keremidi,
    definition: 'Бетонната керемида е от оцветен бетон и прилича на керамичната. По-тежка е, а цветът на повърхността ѝ с времето избледнява.',
  },
  {
    slug: 'turska-keremida', term: 'Турска керемида', cluster: 'pokritiya', synonyms: ['турски керемиди'], owner: S.keremidi,
    definition: 'Турската керемида има формата на полуцилиндър и се реди на два реда: долният събира водата, горният покрива фугите. Среща се по стари къщи и се пренарежда на ръка.',
  },
  {
    slug: 'marsilska-keremida', term: 'Марсилска керемида', cluster: 'pokritiya', synonyms: ['марсилски керемиди'], owner: S.keremidi,
    definition: 'Марсилската керемида е плоска глинена керемида с канали и прегъвки отстрани, които я заключват към съседните. Среща се често по старите къщи в София.',
  },
  {
    slug: 'kapak', term: 'Капак', cluster: 'pokritiya', synonyms: ['билна керемида', 'капаци за покрив'], owner: S.kapaci,
    definition: 'Капакът е полуобла керемида, която покрива билото и ребрата. В оферта за подмазване питайте дали капаците се връщат на разтвор, или на сух монтаж с лента.',
  },
  {
    slug: 'ventilatsionna-keremida', term: 'Вентилационна керемида', cluster: 'pokritiya', owner: B.kondenz,
    definition: 'Вентилационната керемида има отвор с решетка, през който излиза влажният въздух изпод покритието. Слага се близо до билото, по няколко на скат.',
  },
  {
    slug: 'metalni-keremidi', term: 'Метални керемиди', cluster: 'pokritiya', synonyms: ['ламаринени керемиди'], owner: S.metalniKeremidi,
    definition: 'Металните керемиди са ламаринени листове, щамповани да приличат на керемиди. В офертата гледайте дебелината на ламарината и вида на покритието ѝ, защото по тях се различава трайният лист от слабия.',
  },
  {
    slug: 'bitumni-keremidi', term: 'Битумни керемиди', cluster: 'pokritiya', synonyms: ['шинглс'], owner: S.bitumniKeremidi,
    definition: 'Битумните керемиди са гъвкави листове, които се заковават и лепят върху плътна основа. В офертата проверете дали е включена и самата основа от дъски или OSB.',
  },
  {
    slug: 'lamarina-za-pokriv', term: 'Ламарина за покрив', cluster: 'pokritiya', synonyms: ['трапецовидна ламарина', 'поцинкована ламарина'], owner: S.lamarina,
    definition: 'Ламарината за покрив е стоманен лист с цинково или цветно покритие, профилиран като трапец, вълна или фалц. Питайте за дебелината в милиметри и как се уплътняват винтовете.',
  },
  {
    slug: 'termopaneli', term: 'Термопанели', cluster: 'pokritiya', synonyms: ['сандвич панели'], owner: S.termopaneli,
    definition: 'Термопанелите са две ламарини с изолационно ядро между тях. В офертата трябва да пише дебелината на панела и материалът на ядрото.',
  },

  /* 4.4 Изолационни слоеве и хидроизолация */
  {
    slug: 'cherna-hartiya', term: 'Черна хартия', cluster: 'izolaciya', synonyms: ['тер хартия', 'битумна хартия'], owner: own, verify: true, // VERIFY
    definition: 'Черната хартия е картон, напоен с битум, който се слагаше под керемидите като подложка. Спира водата, но не пропуска пара, затова днес мястото ѝ заема подпокривната мембрана.',
  },
  {
    slug: 'holker', term: 'Холкер', cluster: 'izolaciya', owner: own, verify: true, // VERIFY
    definition: 'Холкерът е закръглен или скосен преход от разтвор между плоския покрив и стената или атиката. Слага се, за да не се пречупва хидроизолацията под прав ъгъл и да не се спуква там.',
  },
  {
    slug: 'podpokrivna-membrana', term: 'Подпокривна мембрана', cluster: 'izolaciya', synonyms: ['дишаща мембрана', 'паропропусклива мембрана', 'подкеремидна мембрана', 'подпокривно фолио'], owner: B.membrana,
    definition: 'Подпокривната мембрана е фолиото под керемидите, което спира водата, ако някоя керемида пропусне. В офертата проверете дали е паропропусклива, защото така влагата отдолу излиза навън.',
  },
  {
    slug: 'paroizolatsiya', term: 'Пароизолация', cluster: 'izolaciya', owner: B.izolaciya,
    definition: 'Пароизолацията е фолио от топлата страна на изолацията, което не пуска парата от стаите в нея. Не я бъркайте с паропропускливата мембрана над изолацията: двете вършат обратна работа.',
  },
  {
    slug: 'kamenna-vata', term: 'Каменна вата', cluster: 'izolaciya', synonyms: ['минерална вата'], owner: B.izolaciya,
    definition: 'Каменната вата е топлоизолация от разтопен камък, изтеглен на влакна. Не гори и пропуска пара, затова е обичайният избор между мертеците.',
  },
  {
    slug: 'xps-eps', term: 'XPS / EPS', cluster: 'izolaciya', synonyms: ['екструдиран полистирен', 'експандиран полистирен'], owner: B.izolaciya,
    definition: 'XPS е екструдиран полистирен, твърд и почти непропусклив за вода. EPS е експандираният, по-лекият и по-мекият. Под покрив XPS се слага там, където изолацията носи товар или стои под хидроизолация.',
  },
  {
    slug: 'toploizolatsiya', term: 'Топлоизолация на покрив', cluster: 'izolaciya', synonyms: ['изолация за покрив'], owner: S.toploizolaciya,
    definition: 'Топлоизолацията на покрив е пластът, който задържа топлината в къщата. В офертата трябва да пише материалът, дебелината в сантиметри и дали са включени пароизолацията и вентилацията над нея.',
  },
  {
    slug: 'mostove-na-stud', term: 'Мостове на студ', cluster: 'izolaciya', owner: S.toploizolaciya,
    definition: 'Мостовете на студ са местата, където топлината излиза покрай изолацията, например през греда или процеп. Личат като тъмни петна и конденз по тавана през зимата.',
  },
  {
    slug: 'hidroizolatsiya', term: 'Хидроизолация', cluster: 'izolaciya', owner: S.hidroNaPokriv,
    definition: 'Хидроизолацията е непропускливият пласт върху плосък покрив или тераса. В офертата проверете колко пласта предвижда, от какъв материал и как се обработват бордовете и воронките.',
  },
  {
    slug: 'pvc-membrana', term: 'PVC мембрана', cluster: 'izolaciya', owner: S.hidroNaPokriv,
    definition: 'PVC мембраната е пластмасова хидроизолация, чиито шевове се заваряват с горещ въздух. Слага се на големи плоски покриви и е по-лека от битумната.',
  },
  {
    slug: 'tpo-membrana', term: 'TPO мембрана', cluster: 'izolaciya', owner: S.hidroNaPokriv,
    definition: 'TPO мембраната е хидроизолация от термопластичен полиолефин, близка до PVC, но без омекотители. Шевовете ѝ също се заваряват с горещ въздух.',
  },
  {
    slug: 'epdm-membrana', term: 'EPDM мембрана', cluster: 'izolaciya', owner: S.hidroNaPokriv,
    definition: 'EPDM мембраната е гумена хидроизолация, която се слага на големи цели парчета с малко шевове. Остава еластична на студ и се лепи, а не се заварява.',
  },
  {
    slug: 'bitumna-hidroizolatsiya', term: 'Битумна хидроизолация', cluster: 'izolaciya', synonyms: ['битумна мембрана'], owner: S.bitumna,
    definition: 'Битумната хидроизолация е мушама от битум, залепена с горелка. В офертата проверете колко пласта предвижда, дали горният е с посипка и коя е марката на мушамата.',
  },
  {
    slug: 'voalit', term: 'Воалит', cluster: 'izolaciya', owner: S.bitumna, verify: true, // VERIFY
    verifyNote: 'Дали в практиката се слага само като долен пласт.',
    definition: 'Воалитът е битумна мушама с основа от стъклен воал вместо полиестер. По-малко еластичен е от полиестерната мушама, затова обикновено се слага като долен пласт.',
  },
  {
    slug: 'mushama', term: 'Мушама за покрив', cluster: 'izolaciya', synonyms: ['битумна мушама', 'рубероид'], owner: S.bitumna, verify: true, // VERIFY
    verifyNote: 'Дали „рубероид“ е старото име за мушама на картонена основа.',
    definition: 'Мушамата за покрив е битумен лист на руло. Старото име рубероид се отнася за мушама на картонена основа, която днес почти не се ползва.',
  },
  {
    slug: 'gazoplamachno-zalepvane', term: 'Газопламъчно залепване', cluster: 'izolaciya', owner: S.bitumna,
    definition: 'Газопламъчното залепване е начинът, по който се лепи битумната мушама: долната ѝ страна се разтопява с газова горелка и се притиска към основата. Иска суха основа, затова не се работи в дъжд и по мокра плоча.',
  },
  {
    slug: 'bitumna-lenta', term: 'Битумна лента', cluster: 'izolaciya', owner: S.bitumna,
    definition: 'Битумната лента е самозалепваща лента с битум, с която се затварят дребни пукнатини и преходи. Върши работа за детайли, но не замества хидроизолацията.',
  },
  {
    slug: 'bitumen-grund', term: 'Битумен грунд', cluster: 'izolaciya', synonyms: ['грунд за хидроизолация'], owner: S.bitumna,
    definition: 'Битумният грунд е течен битум, с който се намазва основата преди мушамата, за да се хване тя. Без грунд мушамата се отлепва от прашна или пореста плоча.',
  },
  {
    slug: 'techna-hidroizolatsiya', term: 'Течна хидроизолация', cluster: 'izolaciya', synonyms: ['течна гума'], owner: S.technaGuma,
    definition: 'Течната хидроизолация е покритие, което се нанася с четка или валяк и изсъхва в безшевна ципа. В офертата проверете колко пласта и дали има армираща мрежа по ъглите.',
  },

  /* 4.5 Ламаринени елементи и обшивки */
  {
    slug: 'naduluchna-pola', term: 'Надулучна пола', cluster: 'lamarina', synonyms: ['надулучна ламарина'], owner: own, verify: true, // VERIFY
    definition: 'Надулучната пола е ламаринена лента по стрехата, която минава под последния ред керемиди и пуска водата направо в улука. Без нея водата се стича по челната дъска и тя гние.',
  },
  {
    slug: 'poduluchna-pola', term: 'Подулучна пола', cluster: 'lamarina', synonyms: ['подулучна ламарина'], owner: own, verify: true, // VERIFY
    definition: 'Подулучната пола е ламаринената обшивка под улука, която покрива челната дъска и края на стрехата. Пази дървото от водата, която пръска или прелива от улука.',
  },
  {
    slug: 'streha', term: 'Стреха', cluster: 'lamarina', owner: own,
    definition: 'Стрехата е частта от покрива, която излиза пред стената. Тя отвежда водата далеч от фасадата и пази стената от дъжда.',
  },
  {
    slug: 'chelna-daska', term: 'Челна дъска', cluster: 'lamarina', owner: own, verify: true, // VERIFY
    definition: 'Челната дъска е дъската, закована на края на мертеците по стрехата. На нея се закрепват скобите на улука и подулучната пола.',
  },
  {
    slug: 'atika', term: 'Атика', cluster: 'lamarina', owner: own,
    definition: 'Атиката е ниската стена по ръба на плоския покрив, която стърчи над него. Хидроизолацията се качва по вътрешната ѝ страна, а отгоре атиката се покрива с ламаринена обшивка.',
  },
  {
    slug: 'ulama', term: 'Улама', cluster: 'lamarina', synonyms: ['ендова'], owner: B.ulama, verify: true, // VERIFY
    verifyNote: 'Синонимът „ендова“ чака потвърждение. Ако не се ползва, махнете го.',
    definition: 'Уламата е вътрешният ъгъл, където се срещат два ската, и водата от двата тече по една линия. Затова е едно от най-честите места за теч.',
  },
  {
    slug: 'obshivka-na-streha', term: 'Обшивка на стреха', cluster: 'lamarina', owner: S.obshivkiStrehi,
    definition: 'Обшивката на стреха е ламарината, която затваря долната и челната страна на стрехата. В офертата проверете дали е включена и смяната на гнилата дъска под нея.',
  },
  {
    slug: 'bord', term: 'Борд', cluster: 'lamarina', synonyms: ['странична обшивка', 'обшивка на борд'], owner: S.obshivkiStrehi,
    definition: 'Бордът е страничният ръб на ската при калкана, където покритието свършва. Обшивката му пази ръба от вятъра, който иначе вдига керемидите отдолу.',
  },
  {
    slug: 'zavarshvashta-lamarina', term: 'Завършваща ламарина', cluster: 'lamarina', owner: S.obshivkiStrehi,
    definition: 'Завършващата ламарина е профилът, с който се затваря краят на покритието при стена, борд или било. В офертата обикновено е отделен ред в линейни метри.',
  },
  {
    slug: 'obshivka-na-komin', term: 'Обшивка на комин', cluster: 'lamarina', owner: S.obshivkaKomin,
    definition: 'Обшивката на комин е ламарината около комина, която затваря процепа между зида и покритието. В офертата проверете дали горната лента влиза в зида, или само се лепи с уплътнител отгоре.',
  },
  {
    slug: 'tenekedzhiya', term: 'Тенекеджия', cluster: 'lamarina', synonyms: ['тенекеджийски услуги'], owner: S.tenekedzhiyski,
    definition: 'Тенекеджията е майсторът, който кове и огъва ламарина: обшивки, поли, улуци и шапки за комини. Голяма част от течовете по покрива се оправят точно с тенекеджийска работа.',
  },

  /* 4.6 Улуци и отводняване */
  {
    slug: 'kazanche-za-uluk', term: 'Казанче за улук', cluster: 'uluci', owner: own, verify: true, // VERIFY
    definition: 'Казанчето е събирателната кутия, в която улукът изсипва водата, преди тя да влезе във водосточната тръба. Поема водата при порой, когато тръбата не смогва.',
  },
  {
    slug: 'kapchuk', term: 'Капчук', cluster: 'uluci', owner: own, verify: true, // VERIFY
    definition: 'Капчукът е ръбът на стрехата, откъдето водата капе от покрива, когато няма улук. Със същата дума се нарича и самата вода, която капе от стрехата.',
  },
  {
    slug: 'uluk', term: 'Улук', cluster: 'uluci', synonyms: ['улуци'], owner: S.uluci,
    definition: 'Улукът е коритото по стрехата, което събира водата от покрива. В офертата проверете материала, сечението и наклона, който ще му се даде към тръбите.',
  },
  {
    slug: 'bezshevni-ulutsi', term: 'Безшевни улуци', cluster: 'uluci', owner: S.bezshevni,
    definition: 'Безшевните улуци се изтеглят на място от рулон алуминий в едно парче по цялата стреха. В офертата трябва да пише дебелината на ламарината и през колко сантиметра се слагат скобите.',
  },
  {
    slug: 'pvc-ulutsi', term: 'PVC улуци', cluster: 'uluci', synonyms: ['пластмасови улуци'], owner: S.pvc,
    definition: 'PVC улуците са пластмасови и се сглобяват от парчета с гумени уплътнители. В офертата проверете марката и дали снадките и ъглите са включени.',
  },
  {
    slug: 'potsinkovani-ulutsi', term: 'Поцинковани улуци', cluster: 'uluci', owner: S.pocinkovani,
    definition: 'Поцинкованите улуци са от стоманена ламарина с цинково покритие. Издържат дълго, ако се почистват, а снадките им се запояват или нитоват.',
  },
  {
    slug: 'medni-ulutsi', term: 'Медни и алуминиеви улуци', cluster: 'uluci', owner: S.uluci,
    definition: 'Медните и алуминиевите улуци не ръждясват. Медта потъмнява с времето и се слага по сгради, където видът има значение, а алуминият е по-лекият и по-разпространеният.',
  },
  {
    slug: 'skoba-za-uluk', term: 'Скоба за улук', cluster: 'uluci', synonyms: ['улукодържач'], owner: S.uluci,
    definition: 'Скобата държи улука към челната дъска или мертека. От гъстотата на скобите зависи дали улукът ще издържи снега, затова питайте на какво разстояние се слагат.',
  },
  {
    slug: 'snadka-za-uluk', term: 'Снадка, ъгъл, капачка за улук', cluster: 'uluci', owner: S.uluci,
    definition: 'Снадките, ъглите и капачките са частите, с които улукът се свързва, завива и затваря в краищата. Всяка връзка е възможно място за теч, затова по-малко връзки значи по-малко грижи.',
  },
  {
    slug: 'vodostochna-traba', term: 'Водосточна тръба', cluster: 'uluci', synonyms: ['водосток'], owner: S.vodostochni,
    definition: 'Водосточната тръба сваля водата от улука надолу по фасадата. В офертата проверете колко тръби се предвиждат и докъде се отвежда водата на земята.',
  },
  {
    slug: 'mufa', term: 'Муфа и удължител', cluster: 'uluci', owner: S.vodostochni,
    definition: 'Муфата свързва две парчета водосточна тръба, а удължителят продължава тръбата надолу или настрани. В офертата обикновено влизат като отделни бройки.',
  },
  {
    slug: 'voronka', term: 'Воронка', cluster: 'uluci', owner: B.voronki,
    definition: 'Воронката е отворът на плоския покрив, през който водата влиза в тръбата към канализацията. Обикновено има решетка, която задържа листата.',
  },
  {
    slug: 'pokriven-sifon', term: 'Покривен сифон', cluster: 'uluci', owner: B.voronki, verify: true, // VERIFY
    verifyNote: 'Дали „сифон“ е друго име за воронка, или отделен елемент със затвор срещу миризми.',
    definition: 'Покривният сифон е отводнителят на плоския покрив, през който водата слиза в тръбата. В разговор често се ползва като друго име за воронка.',
  },
  {
    slug: 'prelivnik', term: 'Преливник', cluster: 'uluci', synonyms: ['аварийно отводняване'], owner: B.voronki,
    definition: 'Преливникът е отвор в атиката малко над нивото на покрива. Ако воронката се запуши, водата излиза през него, вместо да се събира на покрива.',
  },
  {
    slug: 'reshetka-za-uluk', term: 'Решетка за улук', cluster: 'uluci', owner: B.ulucite,
    definition: 'Решетката покрива улука отгоре и не пуска вътре листа и клонки. Улукът пак иска почистване, но по-рядко.',
  },

  /* 4.7 Комин, отвори и аксесоари */
  {
    slug: 'malniepriemnik', term: 'Мълниеприемник', cluster: 'aksesoari', owner: own,
    definition: 'Мълниеприемникът е метален прът на най-високата точка на сградата, свързан с проводник към заземяване. Той поема удара на мълнията и го отвежда в земята покрай сградата.',
  },
  {
    slug: 'komin', term: 'Комин', cluster: 'aksesoari', owner: B.shapka,
    definition: 'Коминът е зиданата или металната тръба, през която димът от печката или камината излиза над покрива. По покрива е едно от най-честите места за теч, заради прехода между зида и покритието.',
  },
  {
    slug: 'shapka-za-komin', term: 'Шапка за комин', cluster: 'aksesoari', synonyms: ['капак за комин'], owner: B.shapka,
    definition: 'Шапката за комин е капакът над отвора, който не пуска дъжд и сняг в комина. Пази и зидарията отгоре от напукване.',
  },
  {
    slug: 'kapandura', term: 'Капандура', cluster: 'aksesoari', synonyms: ['покривен прозорец'], owner: S.kapanduri,
    definition: 'Капандурата е прозорец в ската на покрива. В офертата проверете дали е включен самият прозорец, или само монтажът и обшивката около него.',
  },
  {
    slug: 'lyuk-za-pokriv', term: 'Люк за покрив', cluster: 'aksesoari', synonyms: ['ревизионен капак'], owner: hub,
    definition: 'Люкът за покрив е капак в ската или на плоския покрив, през който се излиза горе за ремонт и почистване. За разлика от капандурата обикновено е без стъкло.',
  },
  {
    slug: 'snegozadarzhatel', term: 'Снегозадържател', cluster: 'aksesoari', owner: S.snegozadarzhateli,
    definition: 'Снегозадържателите са тръби или решетки по долния ред на ската, които не дават на снега да падне наведнъж. В офертата проверете към какво се закрепват и на колко реда.',
  },
  {
    slug: 'visulki', term: 'Висулки', cluster: 'aksesoari', owner: B.snyag,
    definition: 'Висулките са ледените шушулки по стрехата. Появяват се, когато топлината от къщата разтопява снега по покрива, а водата замръзва отново на студения ръб.',
  },
  {
    slug: 'aerator', term: 'Аератор', cluster: 'aksesoari', synonyms: ['покривен вентилатор'], owner: B.kondenz,
    definition: 'Аераторът е малък отдушник на покрива, през който излиза влажният въздух изпод покритието. Слага се на плоски покриви и над подпокривното пространство.',
  },

  /* 4.8 Помещения и физика на покрива */
  {
    slug: 'mansarda', term: 'Мансарда', cluster: 'fizika', synonyms: ['тавански етаж'], owner: own,
    definition: 'Мансардата е жилищно помещение под скатния покрив, в което таванът следва наклона на скатовете. За да се живее в нея, покривът отгоре трябва да е топлоизолиран, с пароизолация отвътре и вентилация отгоре.',
  },
  {
    slug: 'podpokrivno-prostranstvo', term: 'Подпокривно пространство', cluster: 'fizika', owner: B.kondenz,
    definition: 'Подпокривното пространство е обемът между покритието и тавана на последния етаж. Ако се проветрява, влагата излиза навън. Ако е затворено, по дървото се събира конденз.',
  },
  {
    slug: 'kondenzatsiya', term: 'Кондензация', cluster: 'fizika', synonyms: ['конденз'], owner: B.kondenz,
    definition: 'Кондензацията е влагата, която се събира по студена повърхност, когато топъл и влажен въздух я докосне. Под покрива личи като капки по мембраната или мокри петна по дървото.',
  },
  {
    slug: 'tech', term: 'Теч', cluster: 'fizika', synonyms: ['теч от покрива'], owner: B.tech,
    definition: 'Течът е вода, която влиза в къщата през покрива. Ако офертата обещава „отстраняване на теча“, питайте как ще се търси мястото, откъдето водата влиза, и какво става, ако то е другаде.',
  },
  {
    slug: 'kozirka', term: 'Козирка', cluster: 'fizika', owner: S.navesi,
    definition: 'Козирката е малък навес над вход, врата или прозорец. Пази вратата от дъжда и се прави от същите материали като навесите.',
  },
];

/* ---------- Отделните страници (фаза 1: P1) ---------- */

/*
 * Всичките 16 термина със звезда, по фази: P1, после P2, после P3.
 * Термин без страница тук стои само в хъба, без връзка.
 */
export const PAGES: GlossaryPage[] = [
  {
    slug: 'naduluchna-pola',
    priority: 'P1',
    verify: true, // VERIFY
    metaDescription: 'Надулучната пола е ламаринената лента по стрехата, която пуска водата от покрива в улука. Къде стои, от какво се прави и какво става, ако липсва.',
    mention: /(?<!\p{L})надулучн(?:а|ата|и|ите)\s+пол(?:а|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Полата върви по цялата дължина на стрехата. Горният ѝ край влиза под мембраната и последния ред керемиди, а долният виси над улука. Водата, която се стича по ската, минава по полата и пада в улука, вместо да намери пролука между керемидата и дъската.',
        ],
      },
      {
        heading: 'Материали',
        paragraphs: [
          'Прави се от поцинкована, цветна или алуминиева ламарина, обикновено в цвета на улука. Тенекеджията я огъва на място по профила на стрехата.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Когато полата липсва или е къса, водата се връща по долната страна на керемидата и мокри челната дъска. След години дъската гние, а улукът се накланя, защото скобите му държат в меко дърво.',
          'Друга честа грешка е полата да легне върху мембраната вместо под нея. Тогава водата, която се стича по мембраната, минава под полата и излиза зад улука.',
        ],
      },
    ],
    related: ['poduluchna-pola', 'uluk', 'streha', 'kapchuk'],
    service: { name: 'Обшивки на стрехи и бордове', href: '/tenekedzhiyski-uslugi/obshivki-strehi-bordove' },
  },
  {
    slug: 'gredored',
    priority: 'P1',
    verify: true, // VERIFY
    metaDescription: 'Гредоредът е редът от дървени греди, които носят пода на тавана. Къде се намира в къщата, какво носи и по какво личи, че гредите са отслабнали.',
    mention: /(?<!\p{L})гредоред(?:а|ът|и|ите)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Гредите стъпват с краищата си върху стените или върху бетонния пояс. В старите къщи гредоредът често е единствената преграда между стаите и подпокривното пространство, затова по него минава и топлоизолацията на тавана.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Гредите гният там, където влизат в зида, особено ако покривът е текъл години наред. Провисване, скърцане при ходене и петна по тавана отдолу показват, че някоя греда е отслабнала.',
          'Преди таванът да стане мансарда, гредоредът се проверява дали ще носи новия под, мебелите и хората в стаята.',
        ],
      },
    ],
    related: ['rigel', 'mertek', 'mansarda', 'pokrivna-konstruktsiya'],
    service: { name: 'Покривна конструкция', href: '/izgrazhdane-na-pokrivi/pokrivna-konstrukciya' },
  },
  {
    slug: 'rigel',
    priority: 'P1',
    verify: true, // VERIFY
    metaDescription: 'Ригелът е хоризонталната греда, която свързва елементите на покривната конструкция. Къде стои, какво поема и по какво личи, че е отслабнал.',
    mention: /(?<!\p{L})ригел(?:а|ът|и|ите)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Ригелът поема част от тежестта на покритието и я предава надолу към столиците. Когато свързва два мертека, той не им дава да се разтворят навън под тежестта на снега.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Ригел, свален при преустройство, за да стане таванът по-висок, без на негово място да се сложи друго укрепване, е честа причина мертеците да провиснат.',
          'Пукнатините по ригела и разхлабените връзки с мертеците се виждат при оглед от тавана, преди да личат отвън по покритието.',
        ],
      },
    ],
    related: ['mertek', 'gredored', 'kleshti', 'rebro'],
    service: { name: 'Покривна конструкция', href: '/izgrazhdane-na-pokrivi/pokrivna-konstrukciya' },
  },
  {
    slug: 'mertek',
    priority: 'P1',
    verify: true, // VERIFY
    metaDescription: 'Мертекът е наклонената греда от билото до стрехата, която носи покритието. Къде стъпва, от какво се прави и по какво личи, че е изгнил.',
    mention: /(?<!\p{L})мерте(?:к|ка|кът|ци|ците)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Горният край на мертека стъпва на билната греда, а долният на мауерлата или пояса над стената. Краят, който излиза пред стената, оформя стрехата, и на него се заковава челната дъска.',
        ],
      },
      {
        heading: 'Материали',
        paragraphs: [
          'Обикновено е от иглолистно дърво, третирано срещу гъби и насекоми. Сечението на мертеците и разстоянието между тях зависят от тежестта на покритието и от снега в района.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Мертеците гният при дълъг теч, най-често около комина, по уламите и в края при стрехата. Скат, който отдалеч изглежда на вълни, обикновено значи отслабнали мертеци. При цялостен ремонт гнилите се сменят или до тях се заковава нова греда.',
        ],
      },
    ],
    related: ['rigel', 'gredored', 'rebro', 'letva'],
    service: { name: 'Покривна конструкция', href: '/izgrazhdane-na-pokrivi/pokrivna-konstrukciya' },
  },
  {
    slug: 'eternit',
    priority: 'P1',
    verify: true, // VERIFY
    metaDescription: 'Етернитът е азбестоцимент, слаган по стари покриви. Къде се среща, защо е опасен при рязане и чупене и кой трябва да го свали.',
    mention: /(?<!\p{L})етернит(?:а|ът|ов|ова|ово|ови|овата|овото|овите)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се среща',
        paragraphs: [
          'Най-често е по покриви на гаражи, навеси, стопански сгради и стари вили. Разпознава се по сивия цвят и вълните, а по-старите плоскости обикновено са покрити с мъх и лишеи.',
        ],
      },
      {
        heading: 'Защо е опасен',
        paragraphs: [
          'Докато плоскостта е цяла, азбестът стои в нея. Опасен е прахът: при рязане, чупене, пробиване и стъргане влакната излизат във въздуха и се вдишват.',
          'Затова етернитът не се сваля самостоятелно. Прави го специализирана фирма със защитни средства, която опакова плоскостите и ги кара до депо, приемащо такъв отпадък.',
        ],
      },
      {
        heading: 'Какво се слага на негово място',
        paragraphs: [
          'След свалянето се проверява дървото отдолу. Новото покритие се избира според това колко тегло носи конструкцията.',
        ],
      },
    ],
    related: ['keremida', 'lamarina-za-pokriv'],
    extraLinks: [{ label: 'Смяна на покрив', href: '/izgrazhdane-na-pokrivi/smyana-na-pokriv' }],
    service: { name: 'Демонтаж на покрив и извозване', href: '/izgrazhdane-na-pokrivi/demontazh-na-pokriv' },
  },
  {
    slug: 'mansarda',
    priority: 'P1',
    metaDescription: 'Мансардата е жилищно помещение под скатния покрив. Как се използва подпокривното пространство и защо в нея става студено или се появява конденз.',
    mention: /(?<!\p{L})мансард(?:а|ата|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Мансардата заема подпокривното пространство, което иначе остава таван за склад. Стените ѝ са ниски отстрани и се издигат към средата, затова полезната площ е по-малка от застроената. Светлината влиза през капандури или през прозорци в калкана.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Най-честият е студ през зимата и жега през лятото, когато изолацията е тънка или има процепи покрай мертеците.',
          'Вторият е конденз. Ако пароизолацията липсва или е пробита, влагата от стаята влиза в изолацията и по дървото се появява мухъл. В старите къщи гредоредът трябва да се провери и дали носи новия под.',
        ],
      },
    ],
    related: ['mansarden-pokriv', 'kapandura', 'gredored', 'paroizolatsiya'],
    service: { name: 'Топлоизолация на покрив', href: '/toploizolaciya-na-pokriv' },
  },
  {
    slug: 'atika',
    priority: 'P1',
    metaDescription: 'Атиката е ниската стена по ръба на плоския покрив. За какво служи, как се връзва с хидроизолацията и защо течовете често започват от нея.',
    mention: /(?<!\p{L})атик(?:а|ата|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Атиката оформя ръба на плоския покрив и не дава на водата да се изсипе по фасадата. Тя насочва водата към воронките, а през отвор в нея излиза преливникът.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Повечето течове при плоските покриви започват от атиката, а не от средата на покрива. Хидроизолацията се отлепва там, където слиза от стената към пода, ако преходът е без холкер.',
          'Обшивката отгоре също се разхлабва с времето. Тогава водата влиза в зида отгоре и слиза по стената в стаите.',
        ],
      },
    ],
    related: ['plosak-pokriv', 'holker', 'voronka'],
    service: { name: 'Хидроизолация на покрив', href: '/hidroizolaciya/na-pokriv' },
  },

  /* ---------- Фаза 2 (P2) ---------- */
  {
    slug: 'poduluchna-pola',
    priority: 'P2',
    verify: true, // VERIFY
    metaDescription: 'Подулучната пола е ламарината под улука, която пази челната дъска и стрехата от вода. Къде стои, по какво се различава от надулучната и кога се сменя.',
    mention: /(?<!\p{L})подулучн(?:а|ата|и|ите)\s+пол(?:а|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Полата се заковава на челната дъска, зад скобите на улука, и слиза под дъното му. Когато улукът прелее при порой или се запуши с листа, водата пада по ламарината, а не по дървото.',
        ],
      },
      {
        heading: 'Разлика между двете поли',
        paragraphs: [
          'Двете поли се допълват. Надулучната е над улука и вкарва водата от покрива в него. Подулучната е под улука и пази дървото, ако водата мине покрай него. В оферта за смяна на улуци проверете дали са включени и двете.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Когато полата липсва, челната дъска гние отдолу и скобите на улука започват да се клатят. Ръждясала или огъната пола пропуска вода зад себе си и вреди повече, отколкото пази.',
        ],
      },
    ],
    related: ['naduluchna-pola', 'uluk', 'chelna-daska'],
    service: { name: 'Обшивки на стрехи и бордове', href: '/tenekedzhiyski-uslugi/obshivki-strehi-bordove' },
  },
  {
    slug: 'streha',
    priority: 'P2',
    metaDescription: 'Стрехата е частта от покрива, която излиза пред стената. За какво служи, от какви части е и какви проблеми се появяват по нея първо.',
    mention: /(?<!\p{L})стрех(?:а|ата|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Стрехата се образува от краищата на мертеците, които продължават пред стената. По нея минава улукът. Колкото е по-широка, толкова по-добре пази фасадата и прозорците от дъжда.',
        ],
      },
      {
        heading: 'От какви части е',
        paragraphs: [
          'Отпред краищата на мертеците са затворени с челна дъска. На нея стоят скобите на улука и подулучната пола. Над улука, под последния ред керемиди, минава надулучната пола. Отдолу стрехата често се затваря с обшивка.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'По стрехата първо личи стар теч: тъмни петна отдолу, гнила челна дъска, увиснал улук. През зимата по нея висят висулки, когато топлината от къщата разтопява снега по покрива.',
        ],
      },
    ],
    related: ['chelna-daska', 'naduluchna-pola', 'uluk', 'obshivka-na-streha'],
    service: { name: 'Обшивки на стрехи и бордове', href: '/tenekedzhiyski-uslugi/obshivki-strehi-bordove' },
  },
  {
    slug: 'kazanche-za-uluk',
    priority: 'P2',
    verify: true, // VERIFY
    metaDescription: 'Казанчето е кутията между улука и водосточната тръба, която поема водата при порой. Къде се слага, от какво се прави и защо прелива.',
    mention: /(?<!\p{L})казанче(?:то|та|тата)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Казанчето стои там, където улукът свършва и започва водосточната тръба. Събира водата от улука, преди тя да слезе надолу, и я задържа за кратко, когато дъждът е по-силен, отколкото тръбата смогва да изведе.',
        ],
      },
      {
        heading: 'Материали',
        paragraphs: [
          'Прави се от същия материал като улука: поцинкована или цветна ламарина, мед или PVC. По старите сгради казанчето често е с украса и се пази при ремонт.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Листата и клонките се събират точно в казанчето и запушват отвора към тръбата. Тогава водата прелива отгоре и мокри фасадата. Затова казанчето се почиства заедно с улука.',
        ],
      },
    ],
    related: ['uluk', 'vodostochna-traba', 'voronka', 'kapchuk'],
    service: { name: 'Водосточни тръби', href: '/uluci/vodostochni-trabi' },
  },
  {
    slug: 'bilo',
    priority: 'P2',
    metaDescription: 'Билото е най-горният ръб на покрива, където се срещат два ската. Какво има под капаците и защо през билото трябва да излиза въздух.',
    // „било“ е и глаголна форма („е било“), затова такива случаи се пропускат.
    mention: /(?<!(?:^|[^\p{L}])(?:е|би|беше|ще)\s)(?<!\p{L})било(?:то)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Билото върви по дължината на покрива, в най-високата му точка. Под него лежи билната греда, на която стъпват горните краища на мертеците. При четирискатния покрив от двата края на билото тръгват ребрата.',
        ],
      },
      {
        heading: 'Защо през билото излиза въздух',
        paragraphs: [
          'Топлият и влажен въздух изпод покритието се качва нагоре. Ако билото е затворено плътно, влагата остава вътре и се събира като конденз по дървото. Затова под капаците се оставя процеп с вентилираща лента: въздухът излиза, а водата не влиза.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Билото е изложено на вятъра от всички страни. Капаците се разклащат, разтворът под тях се напуква и водата влиза отгоре. Провиснало било обикновено значи проблем в конструкцията, а не в покритието.',
        ],
      },
    ],
    related: ['kapak', 'skaten-pokriv', 'ventilatsionna-keremida'],
    service: { name: 'Подмазване на капаци', href: '/remont-na-pokrivi/podmazvane-na-kapaci' },
  },
  {
    slug: 'chelna-daska',
    priority: 'P2',
    verify: true, // VERIFY
    metaDescription: 'Челната дъска е дъската по края на стрехата, на която стои улукът. Къде е, какво носи и по какво личи, че е изгнила.',
    mention: /(?<!\p{L})челн(?:а|ата|и|ите)\s+дъск(?:а|и|ите)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Челната дъска затваря отпред краищата на мертеците и оформя ръба на стрехата. Тя носи скобите на улука, затова от нея зависи дали улукът ще стои прав и с наклон към тръбите.',
        ],
      },
      {
        heading: 'Чести проблеми',
        paragraphs: [
          'Дъската гние, когато водата се стича по нея: ако липсва надулучна пола или улукът прелива. Изгнилата дъска не държи винтовете на скобите и улукът увисва.',
          'Затова при смяна на улуци дъската се проверява, а гнилите участъци се сменят, преди да се сложи новият улук.',
        ],
      },
    ],
    related: ['streha', 'poduluchna-pola', 'uluk'],
    service: { name: 'Обшивки на стрехи и бордове', href: '/tenekedzhiyski-uslugi/obshivki-strehi-bordove' },
  },

  /* ---------- Фаза 3 (P3) ---------- */
  {
    slug: 'cherna-hartiya',
    priority: 'P3',
    verify: true, // VERIFY
    metaDescription: 'Черната хартия е битумна подложка под керемидите на старите покриви. Защо задържа влагата и какво се слага на нейно място при ремонт.',
    mention: /(?<!\p{L})черн(?:а|ата)\s+хартия(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се среща',
        paragraphs: [
          'Черната хартия стои под керемидите на много стари покриви. Слагана е направо върху дъските или мертеците като втора защита, ако някоя керемида пропусне вода.',
        ],
      },
      {
        heading: 'Защо днес не се слага',
        paragraphs: [
          'Хартията спира водата отгоре, но спира и парата отдолу. Влагата от къщата остава под нея и дървото се овлажнява. С годините битумът се напуква, хартията се къса по сгъвките и вече не пази и от водата.',
        ],
      },
      {
        heading: 'Какво се слага на нейно място',
        paragraphs: [
          'При ремонт старата хартия се маха, а мястото ѝ заема паропропусклива подпокривна мембрана.',
        ],
      },
    ],
    related: ['podpokrivna-membrana', 'mushama', 'voalit'],
    service: { name: 'Битумна хидроизолация', href: '/hidroizolaciya/bitumna' },
  },
  {
    slug: 'kapchuk',
    priority: 'P3',
    verify: true, // VERIFY
    metaDescription: 'Капчукът е ръбът на стрехата, откъдето капе водата, когато няма улук. Какво значи думата и защо водата от него вреди на къщата.',
    mention: /(?<!\p{L})капчу(?:к|ка|кът|ци|ците)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира',
        paragraphs: [
          'Капчукът минава по цялата дължина на стрехата. На покрив без улуци водата пада оттам на една линия покрай къщата.',
        ],
      },
      {
        heading: 'Защо е проблем',
        paragraphs: [
          'Водата от капчука удря земята на едно и също място, издълбава я и пръска по цокъла. С годините влагата се качва по стената, а основите стоят мокри.',
          'Затова при къщите с капчук обичайното решение е улук, който събира водата и я отвежда с тръба далеч от стената.',
        ],
      },
    ],
    related: ['uluk', 'streha', 'naduluchna-pola'],
    service: { name: 'Безшевни улуци', href: '/uluci/bezshevni' },
  },
  {
    slug: 'holker',
    priority: 'P3',
    verify: true, // VERIFY
    metaDescription: 'Холкерът е закръгленият преход между плоския покрив и атиката. Защо се прави, от какво е и какво става с хидроизолацията без него.',
    mention: /(?<!\p{L})холкер(?:а|ът|и|ите)?(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Холкерът стои в ъгъла, където подът на плоския покрив среща стена, атика, комин или шахта. Вместо остър ъгъл се получава плавна извивка или скос. Мушамата минава по нея, без да се пречупва, и прилепва по цялата дължина.',
        ],
      },
      {
        heading: 'От какво се прави',
        paragraphs: [
          'Обикновено от циментов разтвор, оформен на място. Има и готови скосени профили, които се лепят в ъгъла.',
        ],
      },
      {
        heading: 'Какво става без него',
        paragraphs: [
          'Пречупена под прав ъгъл, мушамата не прилепва в самия ъгъл и под нея остава кухина. С промените на температурата тя се разтяга, спуква се точно там и водата влиза в зида.',
        ],
      },
    ],
    related: ['atika', 'plosak-pokriv', 'bitumna-hidroizolatsiya'],
    service: { name: 'Битумна хидроизолация', href: '/hidroizolaciya/bitumna' },
  },
  {
    slug: 'malniepriemnik',
    priority: 'P3',
    metaDescription: 'Мълниеприемникът е металният прът на покрива, който поема удара на мълнията. От какви части е и какво става с него при смяна на покрива.',
    mention: /(?<!\p{L})мълниеприемни(?:к|ка|кът|ци|ците)(?!\p{L})/iu,
    sections: [
      {
        heading: 'Къде се намира и за какво служи',
        paragraphs: [
          'Прътът стои на билото или на най-високата точка на покрива, често над комина. От него проводник слиза по покрива и фасадата до заземител в земята. Ударът минава по този път, а не през конструкцията и инсталациите на къщата.',
        ],
      },
      {
        heading: 'Какво става при ремонт',
        paragraphs: [
          'При смяна на покритието мълниеприемникът и проводникът се свалят и после се връщат и свързват наново. Прекъснат или ръждясал проводник прави цялата система безполезна, макар прътът да стои на мястото си.',
          'В оферта за смяна на покрив питайте дали връщането на мълниеприемника е включено.',
        ],
      },
    ],
    related: ['bilo', 'komin'],
    service: { name: 'Смяна на покрив', href: '/izgrazhdane-na-pokrivi/smyana-na-pokriv' },
  },
];
